import { useHandler } from "@redotech/react-util/hook";
import { isEditingLockedStatus } from "@redotech/redo-fulfillment-common/fulfillment-group-status";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { getTeamOMSUsers } from "../utils/get-team-oms-users";
import { formatHoldNotes } from "../utils/hold-notes";

export const FulfillmentNotes = memo(function FulfillmentNotes({
  fulfillmentGroups,
  updateRows,
  everythingDisabled,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  everythingDisabled: boolean;
}) {
  const noteFromBuyer = useMemo(() => {
    if (fulfillmentGroups.length === 0) {
      return "";
    }
    if (fulfillmentGroups.length === 1) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return fulfillmentGroups[0].noteFromBuyer || "";
    }

    // Get all notes (including empty ones)
    const notes = fulfillmentGroups.map(
      ({ noteFromBuyer }) => noteFromBuyer || "",
    );

    // Check if all notes are exactly the same
    const firstNote = notes[0];
    const allSame = notes.every((note) => note === firstNote);

    return allSame ? firstNote : "{mixed}";
  }, [fulfillmentGroups]);

  const setNote = useHandler(
    (key: "noteToBuyer" | "internalNote", value: string) => {
      updateRows(
        fulfillmentGroups.map((group) => ({
          fulfillmentGroupId: group._id,
          [key]: value,
        })),
      );
    },
  );

  const { noteToBuyer, internalNote, marketplaceInternalNote } = useMemo(() => {
    if (fulfillmentGroups.length === 0) {
      return { noteToBuyer: "", internalNote: "", marketplaceInternalNote: "" };
    }

    if (fulfillmentGroups.length === 1) {
      return {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        noteToBuyer: fulfillmentGroups[0].noteToBuyer || "",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        internalNote: fulfillmentGroups[0].internalNote || "",
        marketplaceInternalNote:
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          fulfillmentGroups[0].marketplaceInternalNote || "",
      };
    }

    // Handle noteToBuyer for multiple groups
    const notesToBuyer = fulfillmentGroups.map(
      ({ noteToBuyer }) => noteToBuyer || "",
    );
    const firstNoteToBuyer = notesToBuyer[0];
    const allNotesToBuyerSame = notesToBuyer.every(
      (note) => note === firstNoteToBuyer,
    );

    // Handle internalNote for multiple groups
    const internalNotes = fulfillmentGroups.map(
      ({ internalNote }) => internalNote || "",
    );
    const firstInternalNote = internalNotes[0];
    const allInternalNotesSame = internalNotes.every(
      (note) => note === firstInternalNote,
    );

    // Handle marketplaceInternalNote for multiple groups
    const marketplaceInternalNotes = fulfillmentGroups.map(
      ({ marketplaceInternalNote }) => marketplaceInternalNote || "",
    );
    const firstMarketplaceInternalNote = marketplaceInternalNotes[0];
    const allMarketplaceInternalNotesSame = marketplaceInternalNotes.every(
      (note) => note === firstMarketplaceInternalNote,
    );

    return {
      noteToBuyer: allNotesToBuyerSame ? firstNoteToBuyer : "{mixed}",
      internalNote: allInternalNotesSame ? firstInternalNote : "{mixed}",
      marketplaceInternalNote: allMarketplaceInternalNotesSame
        ? firstMarketplaceInternalNote
        : "{mixed}",
    };
  }, [fulfillmentGroups]);

  const customAttributes = useMemo(() => {
    return fulfillmentGroups.flatMap(({ customAttributes }) =>
      customAttributes.map((customAttribute) => ({
        key: customAttribute.key,
        value: customAttribute.value,
      })),
    );
  }, [fulfillmentGroups]);

  const team = useContext(TeamContext);
  const teamUsers = useMemo(() => (team ? getTeamOMSUsers(team) : []), [team]);

  const holdNotesText = useMemo(() => {
    if (fulfillmentGroups.length === 0) {
      return "";
    }
    const group = fulfillmentGroups[0];
    if (!group) {
      return "";
    }
    const orderName = group.orders?.[0]?.name ?? "";
    return formatHoldNotes(
      group.holds,
      group.holdsPlacedByInfo,
      orderName,
      teamUsers,
    );
  }, [fulfillmentGroups, teamUsers]);

  return (
    <Flex dir="column" gap="3xl" w="full">
      <Flex dir="column" gap="2xl">
        <RedoTextareaInputField
          autoExpand
          disabled
          label="Note from buyer"
          setValue={() => {}} // noop
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          value={noteFromBuyer}
        />
        <RedoTextareaInputField
          autoExpand
          disabled
          label="Marketplace internal note"
          setValue={() => {}} // noop
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          value={marketplaceInternalNote}
        />
        <RedoTextareaInputField
          autoExpand
          disabled={everythingDisabled || fulfillmentGroups.length !== 1}
          label="Internal note"
          setValue={(value) => setNote("internalNote", value)}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          value={internalNote}
        />
        <RedoTextareaInputField
          autoExpand
          disabled={
            everythingDisabled ||
            fulfillmentGroups.length !== 1 ||
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            isEditingLockedStatus(fulfillmentGroups[0].status)
          }
          label="Note to buyer"
          setValue={(value) => setNote("noteToBuyer", value)}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          value={noteToBuyer}
        />
        {holdNotesText && (
          <RedoTextareaInputField
            autoExpand
            disabled
            label="Hold notes"
            setValue={() => {}}
            value={holdNotesText}
          />
        )}
        {customAttributes.map((customAttribute, index) => (
          <Flex
            dir="column"
            gap="xxs"
            key={index}
            style={{ overflowWrap: "anywhere" }}
          >
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {customAttribute?.key}
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {customAttribute?.value}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
});
